import React, { useCallback, useMemo } from 'react'
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow_short_right.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check-circle.svg'
import ViewportDropdown from 'shared/ui/ViewportDropdown/ViewportDropdown'
import DatePickerInput from 'shared/components/DatePickerInput/DatePickerInput'
import { PERIOD_CUSTOM } from '../../utils/reportsPeriods'
import selectStyles from 'shared/ui/SelectDropdown/SelectDropdown.module.scss'
import s from './PeriodSelect.module.scss'

const isCalendarTarget = target =>
	Boolean(
		target?.closest?.('[role="dialog"][aria-label="Calendar"]') ||
			target?.closest?.('[data-reports-period-calendar="true"]'),
	)

/**
 * Reports period dropdown with inline From/To + Apply under Custom period.
 */
export const PeriodSelect = ({
	value,
	onChange,
	options = [],
	customFrom = '',
	customTo = '',
	onCustomFromChange,
	onCustomToChange,
	onApplyCustom,
	canApplyCustom = false,
	customRangeInvalid = false,
	triggerLabel,
	placeholder = 'Select period',
	disabled = false,
	minMenuWidth = 240,
	menuZIndex = 3000,
}) => {
	const selectedLabel = triggerLabel || placeholder
	const customSelected = value === PERIOD_CUSTOM
	const estimatedMenuHeight = Math.max(
		48,
		options.length * 42 + 8 + (customSelected ? 190 : 0),
	)
	const dateFieldClass = `${s.dateField} ${customRangeInvalid ? s.dateFieldInvalid : ''}`.trim()

	const shouldCloseOnSelect = useCallback(nextValue => nextValue !== PERIOD_CUSTOM, [])
	const isOutsideClickIgnored = useCallback(target => isCalendarTarget(target), [])

	const renderOption = useMemo(
		() =>
			({ option, selected, onSelect, close }) => {
				const isCustom = option.value === PERIOD_CUSTOM
				const handleApply = event => {
					event.preventDefault()
					event.stopPropagation()
					const applied = onApplyCustom?.()
					if (applied !== false) close?.()
				}
				return (
					<div
						key={option.value}
						className={`${s.option} ${isCustom ? s.optionCustom : ''}`.trim()}
					>
						<button
							type="button"
							className={`${selectStyles.item} ${
								selected ? selectStyles.itemActiveBg : ''
							}`.trim()}
							role="option"
							aria-selected={selected}
							onClick={onSelect}
						>
							<span className={selectStyles.itemText}>{option.label}</span>
							{selected ? (
								<span className={selectStyles.check} aria-hidden>
									<CheckIcon />
								</span>
							) : null}
						</button>
						{isCustom && selected ? (
							<div
								className={s.customRange}
								role="group"
								aria-label="Custom period dates"
								data-reports-period-calendar="true"
								onMouseDown={event => event.stopPropagation()}
							>
								<label className={s.field}>
									<span className={s.fieldLabel}>From</span>
									<div className={dateFieldClass}>
										<DatePickerInput
											value={customFrom}
											onChange={onCustomFromChange}
											minISO="2000-01-01"
											className={s.dateInput}
											inputClassName={s.dateInputControl}
										/>
									</div>
								</label>
								<label className={s.field}>
									<span className={s.fieldLabel}>To</span>
									<div className={dateFieldClass}>
										<DatePickerInput
											value={customTo}
											onChange={onCustomToChange}
											minISO={customFrom || '2000-01-01'}
											className={s.dateInput}
											inputClassName={s.dateInputControl}
										/>
									</div>
								</label>
								{customRangeInvalid ? (
									<p className={s.error} role="alert">
										The end date must be on or after the start date.
									</p>
								) : null}
								<button
									type="button"
									className={s.applyButton}
									onClick={handleApply}
									disabled={!canApplyCustom}
								>
									Apply
								</button>
							</div>
						) : null}
					</div>
				)
			},
		[
			canApplyCustom,
			customFrom,
			customTo,
			customRangeInvalid,
			dateFieldClass,
			onApplyCustom,
			onCustomFromChange,
			onCustomToChange,
		],
	)

	return (
		<ViewportDropdown
			value={value}
			options={options}
			onChange={onChange}
			disabled={disabled}
			menuClassName={selectStyles.menu}
			estimatedMenuHeight={estimatedMenuHeight}
			minMenuWidth={minMenuWidth}
			menuZIndex={menuZIndex}
			shouldCloseOnSelect={shouldCloseOnSelect}
			isOutsideClickIgnored={isOutsideClickIgnored}
			renderTrigger={({ isOpen, disabled: isDisabled, toggle, triggerRef }) => (
				<div className={`${selectStyles.wrap} ${s.triggerWrap}`.trim()}>
					<button
						ref={triggerRef}
						type="button"
						className={`${selectStyles.trigger} ${isOpen ? selectStyles.open : ''}`.trim()}
						onClick={toggle}
						disabled={isDisabled}
						aria-haspopup="listbox"
						aria-expanded={isOpen}
					>
						<span
							className={`${selectStyles.value} ${!value ? selectStyles.placeholder : ''}`.trim()}
						>
							{selectedLabel}
						</span>
						<span
							className={`${selectStyles.chevron} ${isOpen ? selectStyles.chevronOpen : ''}`.trim()}
							aria-hidden
						>
							<ArrowIcon />
						</span>
					</button>
				</div>
			)}
			renderOption={renderOption}
		/>
	)
}

export default PeriodSelect
